// Utilidades puras compartidas por las vistas de capacidades (Default Capabilities
// y Products). Centralizan la lectura de flags de entorno, la conversión de ritmos
// para líneas lentas y el aplanado de celdas por línea.

export const lineaLenta = process.env.REACT_APP_LINEA_LENTA === 'true';
export const allowPiezas = process.env.REACT_APP_ALLOW_CAP_PIEZAS !== 'false';
export const allowIdeal = process.env.REACT_APP_ALLOW_CAP_VELOCIDAD_IDEAL !== 'false';
export const allowEstandar = process.env.REACT_APP_ALLOW_CAP_VELOCIDAD_ESTANDAR !== 'false';
export const allowRollover = process.env.REACT_APP_ALLOW_CAP_ROLLOVER !== 'false';

// El backend guarda los ritmos como piezas/minuto. En líneas lentas el usuario
// captura minutos/pieza, así que se invierte al mostrar y al enviar.
// NOTA: Math.round(1/v) es lossy (comportamiento heredado de List.jsx); se mantiene
// por consistencia. TODO: si el backend acepta decimales, quitar el redondeo.
export function toDisplay(ritmo) {
    return lineaLenta && ritmo > 0 ? Math.round(1 / ritmo) : ritmo;
}

export function toApi(ritmo) {
    return lineaLenta && ritmo > 0 ? 1 / ritmo : ritmo;
}

// Aplana la respuesta de GET /CeldaPorLinea/CeldaPorLinea?linea=... a {id, nombre}.
// Misma lógica que HomeContext.getCeldas: excluye orden=0 (celda no productiva).
export function flattenCeldas(raw) {
    return (raw || [])
        .flatMap((l) => l.celdas || [])
        .filter((c) => c.orden !== 0)
        .map((c) => ({ id: c.celda, nombre: c.nombreCelda }));
}

// Id de la celda que define el CONTEO de la línea (campo `celda0Conteo` de la celda
// con orden=0, ver contrato CeldaPorLinea). Es la celda "fuente": de ella el backend
// deriva la capacidad de la línea (celda cero). Devuelve null si no viene.
export function celdaConteoDeLinea(raw) {
    const cero = (raw || [])
        .flatMap((l) => l.celdas || [])
        .find((c) => c.orden === 0);
    return cero?.celda0Conteo ?? null;
}

// Diferencia porcentual de un valor de celda respecto al default de línea.
// Devuelve un entero (ej. 4 → "+4%"); null si no hay base o no hay diferencia.
export function deltaPct(valor, base) {
    const v = Number(valor);
    const b = Number(base);
    if (!b || v === b || Number.isNaN(v) || Number.isNaN(b)) return null;
    return Math.round(((v - b) / b) * 100);
}

// Columnas de capacidad activas según flags. Fuente única para el encabezado del
// acordeón y las filas, evitando que se desalineen. `valorRollover` solo aplica a
// la línea (fuente de verdad), nunca por celda.
export function capFields(includeRollover = false) {
    const fields = [];
    // Tiempo/Ritmo ideal y estándar van primero; el resto de campos después.
    // Labels con espacio (no "/") para que el encabezado pueda partir en 2 renglones
    // en columnas angostas (Products) sin desbordarse.
    if (allowIdeal) fields.push({ key: 'ritmoIdeal', label: lineaLenta ? 'Tiempo ideal' : 'Ritmo ideal', lineOnly: false });
    if (allowEstandar) fields.push({ key: 'ritmoEstandar', label: lineaLenta ? 'Tiempo estándar' : 'Ritmo estándar', lineOnly: false });
    if (allowPiezas) fields.push({ key: 'piezasPorPaquete', label: 'Piezas x paq.', lineOnly: false });
    if (includeRollover && allowRollover) fields.push({ key: 'valorRollover', label: 'Rollover', lineOnly: true });
    return fields;
}

// Campos de la fila "capacidad de línea" en Capacidades Default (contrato
// CapacidadesDefaultController). La vista solo EDITA capacidades existentes
// (PUT /Actualizar); la creación (POST /Insertar) no aplica. Por eso:
// - Cuando la capacidad no existe, la fila es solo informativa (base sin editar).
// - `paquetesPorTarima` y `cantPorPieza` solo los acepta PUT /Actualizar; el GET
//   no los devuelve, así que no hay forma de leer el valor persistido — se muestran
//   con el default de esquema (cantPorPieza=1, paquetesPorTarima vacío) y hay que
//   verificarlos antes de guardar.
export function capLineaDefaultFields(existe) {
    // Base (ritmos + piezas) con sus flags ya aplicados, indexada por key.
    const byKey = Object.fromEntries(capFields(false).map((f) => [f.key, f]));
    // Extras editables por celda (no lineOnly). El GET no los expone, así que parten
    // del default de esquema (null / 1); el hint avisa que hay que verificarlos.
    const extras = existe
        ? {
            cantPorPieza: {
                key: 'cantPorPieza', label: 'Cant. x pieza', float: true,
                hint: 'El backend no expone el valor guardado; verifica antes de guardar.',
            },
            paquetesPorTarima: {
                key: 'paquetesPorTarima', label: 'Paq. x tarima', nullable: true,
                hint: 'El backend no expone el valor guardado; verifica antes de guardar.',
            },
        }
        : {};
    // Orden solicitado; se omiten los que no apliquen (flag off o capacidad sin crear).
    const order = ['ritmoIdeal', 'ritmoEstandar', 'cantPorPieza', 'piezasPorPaquete', 'paquetesPorTarima'];
    return order.map((k) => byKey[k] || extras[k]).filter(Boolean);
}
